"use client";

import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import { TextFieldProps } from "@mui/material/TextField";
import clsx from "clsx";

import { SearchPanelTranslations } from "~/bff/types/SearchPanelTranslations";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Search24 } from "~/theme/icons/components/search";

import { SEARCH_FIELD_CLASSES } from "./constants";
import { TextFieldStyled } from "./styled";

export type SearchFieldProps = TextFieldProps & {
  mode?: "button" | "field";
  withIcon?: boolean;
  value?: string;
  valueRevision?: number;
  isSearchOpen?: boolean;
  isSuggestionsOpen?: boolean;
  onEscape?: () => void;
  onClear?: () => void;
  onSearch?: () => void;
  onClick?: () => void;
  onHydratedValue?: (value: string) => void;
  translations?: SearchPanelTranslations | null;
};

export const SearchField: React.FC<SearchFieldProps> = ({
  className,
  mode = "field",
  value,
  valueRevision,
  withIcon = true,
  isSearchOpen,
  isSuggestionsOpen = false,
  onEscape,
  onKeyDown,
  onClear,
  onSearch,
  onClick,
  onHydratedValue,
  translations,
  ...passedProps
}) => {
  const disabled = useMemo(
    () => mode === "button" || passedProps?.inputProps?.disabled, //NOSONAR not a prop defined on MUI component
    [mode, passedProps?.inputProps?.disabled], //NOSONAR not a prop defined on MUI component
  );

  const [rawQuery, setRawQuery] = useState<string>(value ?? "");
  const inputRef = useRef<HTMLInputElement>(undefined);
  const hasHydrated = useRef(false);
  const hasLocalChanges = useRef(false);
  const previousValue = useRef({ value, valueRevision });
  const isShowClear = mode !== "button" && rawQuery;

  const handleClear = useCallback(() => {
    hasLocalChanges.current = true;
    setRawQuery("");
    if (onClear) {
      onClear();
    }
    inputRef.current?.focus();
  }, [onClear]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (mode === "button" && (event.key === "Enter" || event.key === " ")) {
        if (onClick) {
          onClick();
          return;
        }
      }

      if (onEscape && onClear && event.key === "Escape") {
        if (onEscape) {
          onEscape();
        }
        if (onClear) {
          onClear();
        }
      }
      if (event.key === "Enter" && rawQuery?.trim()) {
        if (onEscape) {
          onEscape();
        }
        if (onSearch) {
          onSearch();
        }
      }
      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [onClear, onEscape, onKeyDown, onSearch, onClick, rawQuery, mode],
  );

  const handleIconClick = useCallback(() => {
    if (isSearchOpen && rawQuery.trim()) {
      onSearch?.();
      onEscape?.();
    }
  }, [isSearchOpen, onEscape, onSearch, rawQuery]);

  useLayoutEffect(() => {
    if (hasHydrated.current) {
      return;
    }
    hasHydrated.current = true;

    const hydratedValue = inputRef.current?.value;

    if (
      mode === "field" &&
      hydratedValue !== undefined &&
      hydratedValue !== rawQuery
    ) {
      hasLocalChanges.current = true;
      setRawQuery(hydratedValue);
      onHydratedValue?.(hydratedValue);
    }
  }, [mode, onHydratedValue, rawQuery]);

  useLayoutEffect(() => {
    const previous = previousValue.current;
    previousValue.current = { value, valueRevision };
    const isExplicitChange = previous.valueRevision !== valueRevision;

    if (previous.value === value && !isExplicitChange) {
      return;
    }

    if (
      valueRevision !== undefined &&
      hasLocalChanges.current &&
      !isExplicitChange &&
      value !== inputRef.current?.value
    ) {
      return;
    }

    hasLocalChanges.current = false;
    setRawQuery(value ?? "");
  }, [value, valueRevision]);

  useEffect(() => {
    mode === "field" && setTimeout(() => inputRef.current?.focus());
  }, [mode]);

  return (
    <TextFieldStyled //NOSONAR ARIA is acceptable to be used here
      id="search-field"
      role="combobox"
      aria-autocomplete="list"
      aria-haspopup="listbox"
      aria-expanded={isSuggestionsOpen}
      autoComplete="off"
      {...passedProps}
      onChange={(e) => {
        hasLocalChanges.current = true;
        setRawQuery(e.target.value);
        passedProps?.onChange && passedProps?.onChange(e);
      }}
      value={rawQuery}
      {...getTestAutomationProps("search-field")}
      {...(mode === "button" && {
        tabIndex: passedProps.tabIndex ?? 0,
        "aria-label": translations?.searchText ?? "",
      })}
      variant="standard"
      placeholder={translations?.searchText ?? ""}
      className={clsx(className, {
        [SEARCH_FIELD_CLASSES.buttonMode]: mode === "button",
      })}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      inputRef={inputRef}
      slotProps={{
        htmlInput: {
          ...passedProps?.inputProps, //NOSONAR not a prop defined on MUI component
          value: rawQuery,
          disabled,
          "data-testautomation-id": "search-field-input",
          "aria-label": translations?.searchText ?? "",
          "aria-hidden": mode === "button",
          "aria-autocomplete": "list",
        },
        input: {
          className: SEARCH_FIELD_CLASSES.inputBase,
          startAdornment: withIcon && (
            <InputAdornment
              className={SEARCH_FIELD_CLASSES.icon}
              onClick={handleIconClick}
              position="start"
            >
              <Search24 dataTestautomationId="search-icon" />
            </InputAdornment>
          ),
          endAdornment: isShowClear ? (
            <InputAdornment position="end">
              <Button
                variant="text"
                className={SEARCH_FIELD_CLASSES.clear}
                onClick={handleClear}
              >
                {translations?.clearAllText}
              </Button>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
};
